import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiCheck, FiShoppingCart, FiZap } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Lottie from 'lottie-react';
import productAnim from '../../animatesingup.json';
import '../../csscomponent/ProductPage.css';
import { publicAsset } from '../../lib/publicPath';
import { getCmsProducts, getCmsSettings, saveCartItem } from '../../lib/sqlAuth';
import { subscribeCmsUpdates } from '../../lib/cmsSync';
import AdMobBanner from '../AdMobBanner';

const FALLBACK_PROJECTS = [
  {
    id: 'prod_vaishnavi',
    imageURL: '/images/vaishnavi.png',
    name: 'Vaishnavi Laser',
    description: 'A dynamic website crafted to showcase laser cutting and engraving services with a clean conversion-focused structure.',
    price: 9999,
    originalPrice: 14999,
    linkUrl: '',
  },
  {
    id: 'prod_resumegen',
    imageURL: '/images/resumegen.png',
    name: 'ResumeGen',
    description: 'A resume builder experience that helps users generate personalized, professional CVs in minutes.',
    price: 4999,
    originalPrice: 7999,
    linkUrl: '',
  },
  {
    id: 'prod_php_banking',
    imageURL: '/images/PHP.jpg',
    name: 'PHP Banking Website',
    description: 'A functional banking workflow demo covering transfers, transaction history, and account interactions.',
    price: 14999,
    originalPrice: 22000,
    linkUrl: '',
  },
  {
    id: 'prod_jonfinder',
    imageURL: '/images/jonfinder.jpeg',
    name: 'JonFinder',
    description: 'A job and profile discovery concept built to organize listings, quick filters, and application journeys.',
    price: 12499,
    originalPrice: 18000,
    linkUrl: '',
  },
  {
    id: 'prod_webchat',
    imageURL: '/images/webchat.jpeg',
    name: 'WebChat Support',
    description: 'A customer support chat interface concept designed for fast replies, lead capture, and ongoing engagement.',
    price: 6999,
    originalPrice: 10000,
    linkUrl: '',
  },
  {
    id: 'prod_resume_review',
    imageURL: '/images/Resume.jpeg',
    name: 'Resume Review Landing',
    description: 'A conversion-focused landing page concept for resume review and profile improvement services.',
    price: 3999,
    originalPrice: 5500,
    linkUrl: '',
  },
];

function ProductPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [addedIds, setAddedIds] = useState({});
  const [content, setContent] = useState({
    eyebrow: 'Products',
    title: 'Selected Work and Product Experiments',
    description: 'We build production-grade products and functional prototypes with the same engineering rigor.',
    tail: 'Want a similar product for your business? Let’s discuss your roadmap.',
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [productsRes, settingsRes] = await Promise.all([
          getCmsProducts(),
          getCmsSettings({ keys: ['products_intro'] }),
        ]);
        if (!mounted) return;
        const nextProjects = Array.isArray(productsRes?.items) ? productsRes.items.map((item, idx) => ({
          id: item.id || `prod_${idx}`,
          imageURL: item.image,
          name: item.name,
          description: item.description,
          price: Number(item.price) || FALLBACK_PROJECTS[idx % FALLBACK_PROJECTS.length]?.price || 7999,
          originalPrice: Number(item.original_price) || FALLBACK_PROJECTS[idx % FALLBACK_PROJECTS.length]?.originalPrice || 11999,
          linkUrl: item.link_url,
        })) : [];
        setProjects(nextProjects.length ? nextProjects : FALLBACK_PROJECTS);
        const intro = settingsRes?.settings?.products_intro || {};
        setContent((prev) => ({ ...prev, ...intro }));
      } catch (_) {
        if (mounted) {
          setProjects(FALLBACK_PROJECTS);
        }
      }
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'products'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const handleAddToCart = async (project, e) => {
    if (e) e.stopPropagation();
    const itemPrice = project.price || 4999;
    try {
      await saveCartItem({
        packageId: project.id || `prod_${project.name.toLowerCase().replace(/\s+/g, '_')}`,
        packageName: project.name,
        priceInr: itemPrice,
        quantity: 1,
      });
      setAddedIds((prev) => ({ ...prev, [project.name]: true }));
      toast.success(`${project.name} added to cart!`);
      setTimeout(() => {
        setAddedIds((prev) => ({ ...prev, [project.name]: false }));
      }, 1800);
    } catch (_) {
      toast.error('Unable to add product to cart right now.');
    }
  };

  const handleBuyNow = async (project, e) => {
    if (e) e.stopPropagation();
    await handleAddToCart(project);
    navigate('/dashboard/cart');
  };

  return (
    <section className="product-page">
      <div className="product-head">
        <div>
          <p>{content.eyebrow}</p>
          <h1>{content.title}</h1>
          <span>{content.description}</span>
        </div>
        <div className="product-lottie-wrap">
          <Lottie animationData={productAnim} loop className="product-lottie" />
        </div>
      </div>

      <div className="product-grid">
        {projects.map((project, index) => {
          const price = project.price || 4999;
          const orig = project.originalPrice || Math.round(price * 1.4);
          const discountPct = Math.round(((orig - price) / orig) * 100);
          const isAdded = Boolean(addedIds[project.name]);

          return (
            <motion.article
              key={project.name}
              className="product-card"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
            >
              <div className="product-media">
                <img
                  src={
                    project.imageURL
                      ? project.imageURL.startsWith('http') || project.imageURL.startsWith('/uploads/')
                        ? project.imageURL
                        : publicAsset(project.imageURL)
                      : publicAsset('images/vaishnavi.png')
                  }
                  alt={project.name}
                />
                <div className="product-hover-panel">
                  <span className="product-hover-kicker">Project Details</span>
                  <p>{project.description}</p>
                </div>
              </div>
              <div className="product-copy">
                <h3>{project.name}</h3>
                <p>{project.description}</p>

                <div className="product-price-row">
                  <span className="product-current-price">₹{price.toLocaleString('en-IN')}</span>
                  {orig > price ? (
                    <>
                      <span className="product-original-price">₹{orig.toLocaleString('en-IN')}</span>
                      <span className="product-discount-badge">{discountPct}% OFF</span>
                    </>
                  ) : null}
                </div>

                <div className="product-actions-row">
                  <button
                    type="button"
                    className={`product-add-cart-btn ${isAdded ? 'added' : ''}`}
                    onClick={(e) => handleAddToCart(project, e)}
                    aria-label={`Add ${project.name} to cart`}
                  >
                    {isAdded ? (
                      <>
                        <FiCheck aria-hidden="true" /> Added
                      </>
                    ) : (
                      <>
                        <FiShoppingCart aria-hidden="true" /> Add to Cart
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    className="product-buy-now-btn"
                    onClick={(e) => handleBuyNow(project, e)}
                    aria-label={`Buy ${project.name} now`}
                  >
                    <FiZap aria-hidden="true" /> Buy Now
                  </button>
                </div>
              </div>
            </motion.article>
          );
        })}
      </div>

      <AdMobBanner title="Digital Products & Tools" subtitle="Verified templates, systems & product architectures" />

      <p className="product-tail">{content.tail}</p>
    </section>
  );
}

export default ProductPage;
